// src/services/auth.js
import { auth, db } from './firebase';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
} from "firebase/auth";
import { 
    doc, 
    setDoc, 
    getDoc,
    collection,
    query,
    where,
    getDocs
} from "firebase/firestore";

// Validate SA ID number (simple check)
export const validateSAIDNumber = (idNumber) => {
    idNumber = idNumber.toString().trim();
    
    if (!idNumber) {
        return { valid: false, message: "ID number is required" };
    }
    
    if (!/^\d{13}$/.test(idNumber)) {
        return { valid: false, message: "ID number must be exactly 13 digits" };
    }
    
    return { valid: true, message: "Valid ID number format" };
};

// Check if ID number exists
export const checkIdNumberExists = async (idNumber) => {
    try {
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("idNumber", "==", idNumber));
        const querySnapshot = await getDocs(q);
        return !querySnapshot.empty;
    } catch (error) {
        console.error("Error checking ID:", error);
        return false;
    }
};

// Sign Up - Fixed
export const signUp = async (email, password, userData) => {
    try {
        // Check if user already exists in Firebase Auth
        try {
            // Try to sign in first to check if account exists
            await signInWithEmailAndPassword(auth, email, password);
            return { success: false, error: "Email already registered. Please login instead." };
        } catch (signInError) {
            // User doesn't exist, proceed with signup
            if (signInError.code !== 'auth/user-not-found') {
                // Some other error occurred
                console.error("Sign in check error:", signInError);
            }
        }

        // Create the user
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Prepare user data
        const userDocData = {
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            email: email,
            phoneNumber: userData.phoneNumber || '',
            role: userData.role || 'candidate',
            status: 'active',
            createdAt: new Date().toISOString(),
            uid: user.uid
        };

        // Save to Firestore - using set with merge to avoid overwriting
        await setDoc(doc(db, "users", user.uid), userDocData, { merge: true });

        // Also create a candidates document if role is candidate
        if (userData.role !== 'recruiter') {
            await setDoc(doc(db, "candidates", user.uid), {
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                email: email,
                createdAt: new Date().toISOString(),
                profileCompleted: false
            }, { merge: true });
        }

        return { success: true, user };
    } catch (error) {
        console.error("Sign up error:", error);
        
        if (error.code === 'auth/email-already-in-use') {
            return { success: false, error: "Email already registered. Please login instead." };
        } else if (error.code === 'auth/weak-password') {
            return { success: false, error: "Password should be at least 6 characters." };
        } else if (error.code === 'auth/too-many-requests') {
            return { success: false, error: "Too many requests. Please try again later." };
        } else if (error.code === 'auth/network-request-failed') {
            return { success: false, error: "Network error. Please check your internet connection." };
        }
        
        return { success: false, error: "Sign up failed. Please try again." };
    }
};

// Sign In - Fixed with better error messages
export const signIn = async (email, password) => {
    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        
        // Check if user document exists
        const userDoc = await getDoc(doc(db, "users", userCredential.user.uid));
        if (!userDoc.exists()) {
            // Create user document if missing
            await setDoc(doc(db, "users", userCredential.user.uid), {
                email: email,
                role: 'candidate',
                status: 'active',
                createdAt: new Date().toISOString(),
                uid: userCredential.user.uid
            }, { merge: true });
        }
        
        return { success: true, user: userCredential.user };
    } catch (error) {
        console.error("Sign in error:", error);
        
        if (error.code === 'auth/user-not-found') {
            return { success: false, error: "No account found with this email. Please sign up first." };
        } else if (error.code === 'auth/wrong-password') {
            return { success: false, error: "Incorrect password. Please try again." };
        } else if (error.code === 'auth/too-many-requests') {
            return { success: false, error: "Too many failed attempts. Please try again later." };
        } else if (error.code === 'auth/network-request-failed') {
            return { success: false, error: "Network error. Please check your internet connection." };
        } else if (error.code === 'auth/invalid-email') {
            return { success: false, error: "Invalid email address format." };
        } else if (error.code === 'auth/user-disabled') {
            return { success: false, error: "This account has been disabled. Please contact support." };
        }
        
        return { success: false, error: "Login failed. Please try again." };
    }
};

// Sign Out
export const signOutUser = async () => {
    try {
        await signOut(auth);
        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// Reset Password
export const resetPassword = async (email) => {
    try {
        await sendPasswordResetEmail(auth, email);
        return { success: true, message: "Password reset email sent. Check your inbox." };
    } catch (error) {
        console.error("Reset password error:", error);
        if (error.code === 'auth/user-not-found') {
            return { success: false, error: "No account found with this email address." };
        } else if (error.code === 'auth/invalid-email') {
            return { success: false, error: "Please enter a valid email address." };
        } else if (error.code === 'auth/too-many-requests') {
            return { success: false, error: "Too many requests. Please try again later." };
        }
        return { success: false, error: "Failed to send reset email. Please try again." };
    }
};